import { useEffect, useRef } from 'react';

export interface UseAutoRedirectOnEmptyPageParams {
  currentPage: number;
  /** Array of items on the current page */
  items: any[];
  meta: {
    totalItems?: number;
    totalPages?: number;
  } | null;
  isLoading: boolean;
  isAssigning: boolean;
  onNavigate: (page: number) => void;
  redirectDelay?: number;
}

/**
 * Hook that automatically redirects users to page 1 when the current page
 * becomes empty after a flow assignment operation.
 *
 * Features:
 * - Detects when page becomes empty after assignment
 * - Implements 1-second delay to show success state
 * - Handles cleanup on unmount
 * - Prevents redirect on page 1
 * - Prevents redirect when all flows are assigned
 */
export const useAutoRedirectOnEmptyPage = (props: UseAutoRedirectOnEmptyPageParams): void => {
  const {
    currentPage,
    items,
    meta,
    isLoading,
    isAssigning,
    onNavigate,
    redirectDelay = 500,
  } = props;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const isNotFirstPage = currentPage > 1;
    const isPageEmpty = items.length === 0;
    const hasFinishedLoading = !isLoading && !isAssigning;
    const hasMoreFlowsInSystem = meta?.totalItems !== undefined && meta.totalItems > 0;

    const shouldRedirect =
      isNotFirstPage && isPageEmpty && hasFinishedLoading && hasMoreFlowsInSystem;

    if (shouldRedirect) {
      timeoutRef.current = setTimeout(() => {
        onNavigate(1);
        timeoutRef.current = null;
      }, redirectDelay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    currentPage,
    items.length,
    isLoading,
    isAssigning,
    meta?.totalItems,
    onNavigate,
    redirectDelay,
  ]);
};
